import { AxiosResponse } from 'axios';
import { BaseService } from '@services/route.service';

export interface LeadFormsConnection {
    connected: boolean;
    pageId: string | null;
    pageName: string | null;
    formId: string | null;
    formName: string | null;
    connectedAt: string | null;
    // O que a Meta ainda não concedeu; vazio = pode seguir.
    missingPermissions: string[];
}

export interface MetaPage {
    id: string;
    name: string;
}

export interface MetaForm {
    id: string;
    name: string;
    status: string;
    leadsCount: number;
}

// mappedTo vem resolvido pelo servidor (por nome, tolerante a variação); a tela só mostra o efeito.
export interface MetaFormField {
    key: string;
    label: string;
    mappedTo: string;
}

/// Tipos da Meta; FULL_NAME, EMAIL e PHONE o CRM reconhece, o resto vai ao histórico.
export type MetaQuestionType =
    | 'FULL_NAME' | 'FIRST_NAME' | 'LAST_NAME' | 'EMAIL' | 'PHONE' | 'COMPANY_NAME'
    | 'JOB_TITLE' | 'CITY' | 'STATE' | 'STREET_ADDRESS' | 'ZIP' | 'CUSTOM';

export interface NewMetaQuestion {
    type: MetaQuestionType;
    /// Obrigatório em CUSTOM: é o texto que o contato lê.
    label?: string;
    options?: string[];
}

export interface CreateMetaFormRequest {
    name: string;
    questions: NewMetaQuestion[];
    privacyPolicyUrl: string;
    privacyPolicyLinkText?: string;
    contextTitle?: string;
    contextItems?: string[];
    thankYouTitle?: string;
    thankYouBody?: string;
    /// Vazio = agradecimento sem botão; a Meta recusa botão sem texto.
    thankYouButtonText?: string;
    followUpUrl?: string;
}

export class LeadFormsService extends BaseService {
    protected get resourceName(): string {
        return 'crm/lead-forms';
    }

    public async getConnection(): Promise<AxiosResponse<{ data: LeadFormsConnection }>> {
        return await this.get('connection');
    }

    public async getPages(): Promise<AxiosResponse<{ data: MetaPage[] }>> {
        return await this.get('pages');
    }

    public async getForms(pageId: string): Promise<AxiosResponse<{ data: MetaForm[] }>> {
        return await this.get(`pages/${encodeURIComponent(pageId)}/forms`);
    }

    /// Cria o formulário na página; o anúncio segue sendo montado no Gerenciador da Meta.
    public async createForm(
        pageId: string,
        body: CreateMetaFormRequest
    ): Promise<AxiosResponse<{ data: { id: string; name: string } }>> {
        return await this.post(body, `pages/${encodeURIComponent(pageId)}/forms`);
    }

    /// A Meta não apaga formulário de cadastro: só arquiva.
    public async archiveForm(
        pageId: string,
        formId: string
    ): Promise<AxiosResponse<unknown>> {
        return await this.post(
            undefined,
            `pages/${encodeURIComponent(pageId)}/forms/${encodeURIComponent(formId)}/archive`);
    }

    public async getFields(formId: string): Promise<AxiosResponse<{ data: MetaFormField[] }>> {
        return await this.get(`forms/${encodeURIComponent(formId)}/fields`);
    }

    public async activate(body: {
        pageId: string;
        pageName: string;
        formId?: string;
        formName?: string;
    }): Promise<AxiosResponse<unknown>> {
        return await this.post(body, 'activate');
    }
}
